<template>
	<widget-settings-layout ref="layout" v-if="settings" slug="chat" name="Chat" :error="saveError" :saving="saving" :has-unsaved-changes="hasUnsavedChanges" show-audio :settings="settings" :saved-settings-json="savedSettingsJson" @save="save">
		<b-card title="Options" class="mb-3">
			<b-form-row>
				<b-col>
					<b-form-checkbox switch inline v-model="settings.enableTTS">Enable text-to-speech</b-form-checkbox>
				</b-col>
				<b-col>
					<b-form-checkbox switch inline v-model="settings.enableEventSounds">Enable event sounds</b-form-checkbox>
				</b-col>
			</b-form-row>
		</b-card>
		<audio-settings :settings="settings" />
		<b-card title="Animation" class="mb-3">
			<b-form-row>
				<b-col>
					<b-form-group label="In Transition">
						<b-form-select v-model="settings.inTransition" :options="inTransitions"></b-form-select>
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Out Transition">
						<b-form-select v-model="settings.outTransition" :options="outTransitions"></b-form-select>
					</b-form-group>
				</b-col>
			</b-form-row>
			<b-form-row>
				<b-col>
					<b-form-group label="Animation duration" append="ms">
						<b-input-group append="ms">
							<b-form-input type="number" min="0" max="5000" v-model="settings.duration"></b-form-input>
						</b-input-group>
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Linger duration" :disabled="!settings.outTransition">
						<b-input-group append="ms">
							<b-form-input type="number" min="0" max="60000" v-model="settings.linger"></b-form-input>
						</b-input-group>
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Load timeout">
						<b-input-group append="ms">
							<b-form-input type="number" min="0" max="100000" step="1" v-model="settings.loadTimeout"></b-form-input>
						</b-input-group>
					</b-form-group>
				</b-col>
			</b-form-row>
		</b-card>
		<text-settings :settings="settings" />
		<b-card title="Colors" class="mb-3">
			<b-form-row>
				<b-col>
					<b-form-group label="Text Color">
						<color-picker v-model="settings.textColor" />
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Message Background">
						<color-picker v-model="settings.textBg" />
					</b-form-group>
				</b-col>
			</b-form-row>
			<b-form-row>
				<b-col>
					<b-form-group label="Gift Text Color">
						<color-picker v-model="settings.giftColor" />
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Gift Background">
						<color-picker v-model="settings.giftBg" />
					</b-form-group>
				</b-col>
			</b-form-row>
		</b-card>
		<b-card title="Outline" class="mb-3">
			<b-form-row>
				<b-col>
					<b-form-group label="Text outline blur">
						<b-input-group append="px">
							<b-form-input type="number" min="0" max="100" step="any" v-model="settings.blurRadius"></b-form-input>
						</b-input-group>
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Text outline size">
						<b-input-group append="px">
							<b-form-input type="number" min="0" max="10" step="any" v-model="settings.spreadRadius"></b-form-input>
						</b-input-group>
					</b-form-group>
				</b-col>
				<b-col cols="6">
					<b-form-group label="Text Outline Color">
						<color-picker v-model="settings.shadowColor" />
					</b-form-group>
				</b-col>
			</b-form-row>
			<b-form-row class="mt-2">
				<b-col>
					<b-form-checkbox switch v-model="settings.enableSpeakingHighlight">Custom speaking outline</b-form-checkbox>
					<color-picker :disabled="!settings.enableSpeakingHighlight" v-model="settings.speakingOutlineColor" class="mt-2" />
				</b-col>
				<b-col>
					<b-form-checkbox switch v-model="settings.enableLoadingHighlight">Custom loading outline</b-form-checkbox>
					<color-picker :disabled="!settings.enableLoadingHighlight" v-model="settings.loadingOutlineColor" class="mt-2" />
				</b-col>
			</b-form-row>
		</b-card>
		<b-card title="Padding" class="mb-3">
			<b-form-row>
				<b-col>
					<b-form-group label="Left" append="px">
						<b-input-group append="px">
							<b-input type="number" min="0" max="2000" v-model="settings.paddingLeft"/>
						</b-input-group>
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Bottom" append="px">
						<b-input-group append="px">
							<b-input type="number" min="0" max="2000" v-model="settings.paddingBottom"/>
						</b-input-group>
					</b-form-group>
				</b-col>
				<b-col>
					<b-form-group label="Right" append="px">
						<b-input-group append="px">
							<b-input type="number" min="0" max="2000" v-model="settings.paddingRight"/>
						</b-input-group>
					</b-form-group>
				</b-col>
			</b-form-row>
		</b-card>
		<b-card class="mb-3">
			<b-form-group label="Ignore chatters" description="One chatter per line">
				<b-textarea v-model="settings.ignoreUsers"></b-textarea>
			</b-form-group>
			<b-form-checkbox v-model="settings.showBotMessages">Show bot messages</b-form-checkbox>
			<b-form-checkbox v-model="settings.showUserCommands">Show user commands</b-form-checkbox>
		</b-card>
		<hide-channels v-model="settings.excludedChannels" :channels="channels" />
		<b-card class="mb-3">
			<b-form-group label="Extra CSS">
				<b-textarea v-model="settings.extraCSS"></b-textarea>
			</b-form-group>
		</b-card>
		<template #preview="{ previewAudio }">
			<chat-widget-preview :settings="settings" :speed="previewSpeed / 100" :preview-audio="previewAudio" />
		</template>
		<template #preview-options>
			<div class="d-flex align-items-center mt-2">
				<small class="text-muted preview-slider-label">Speed:</small>
				<b-form-input type="range" min="0" max="100" step="1" v-model.number="previewSpeed" class="mx-2 flex-grow-1" />
				<small class="text-muted preview-slider-value">{{ previewSpeedLabel }}</small>
			</div>
		</template>
	</widget-settings-layout>
</template>

<script>
import animateCssTransitions from '@/animateCssTransitions';
import ColorPicker from '@/components/ColorPicker.vue';
import WidgetSettingsLayout from '@/components/Widget/WidgetSettingsLayout.vue';
import ChatWidgetPreview from '@/components/Widget/ChatWidgetPreview.vue';
import TextSettings from '@/components/Widget/TextSettings.vue';
import AudioSettings from '@/components/Widget/AudioSettings.vue';
import HideChannels from '@/components/Widget/HideChannels.vue';

export default {
	beforeRouteLeave(to, from, next) {
		if (!this.hasUnsavedChanges) return next();
		this.$refs.layout.showLeaveModal(next, () => this.save());
	},
	data() { return {
		loading: 0,
		error: null,
		saving: 0,
		saveError: null,
		settings: null,
		channels: [],
		previewSpeed: 50,
		inTransitions: [ ...[{ text: 'Don\'t fade in', value: null }], ...animateCssTransitions.inTransitions ],
		outTransitions: [ ...[{ text: 'Don\'t fade out (log mode)', value: null }], ...animateCssTransitions.outTransitions ],
		curColor: {},
		savedSettingsJson: null,
	}; },
	components: {
		ColorPicker,
		ChatWidgetPreview,
		WidgetSettingsLayout,
		TextSettings,
		AudioSettings,
		HideChannels,
	},
	async mounted() {
		window.addEventListener('beforeunload', this._onBeforeUnload);
		try {
			this.saving = 0;
			this.saveError = null
			this.loading++;
			this.error = null;
			const [settings, channels] = await Promise.all([
				this.$api.get('widgetSettings/chat'),
				this.$api.get('channels/widgets').catch(() => []),
			]);
			this.channels = channels;
			this.settings = {
				...settings,
				enableTTS: Boolean(settings.enableTTS),
				enableEventSounds: Boolean(settings.enableEventSounds),
				volume: settings.volume ?? 100,
				compressionAmount: settings.compressionAmount ?? 0,
				inTransition: settings.inTransition||null,
				outTransition: settings.outTransition||null,
				textColor: settings.textColor || 'rgba(255, 255, 255, 1)',
				shadowColor: settings.shadowColor || 'rgba(0, 0, 0, 1)',
				textBg: settings.textBg || 'rgba(0, 128, 0, 1)',
				giftColor: settings.giftColor || 'rgba(0, 0, 0, 1)',
				giftBg: settings.giftBg || 'rgba(255, 195, 0, 1)',
				fontFamily: settings.fontFamily || '',
				fontSize: settings.fontSize || 0,
				blurRadius: settings.blurRadius || 0,
				lineHeight: settings.lineHeight || 0,
				letterSpacing: settings.letterSpacing || 0,
				textAlign: settings.textAlign || 'left',
				boldText: Boolean(settings.boldText),
				italicText: Boolean(settings.italicText),
				underlineText: Boolean(settings.underlineText),
				strikethroughText: Boolean(settings.strikethroughText),
				uppercaseText: Boolean(settings.uppercaseText),
				ignoreUsers: settings.ignoreUsers ? settings.ignoreUsers.join('\n') : '',
				showBotMessages: Boolean(settings.showBotMessages),
				showUserCommands: Boolean(settings.showUserCommands),
				extraCSS: settings.extraCSS || '',
				customFont: Boolean(settings.customFont),
				paddingLeft: settings.paddingLeft || 0,
				paddingBottom: settings.paddingBottom || 0,
				paddingRight: settings.paddingRight || 0,
				enableSpeakingHighlight: Boolean(settings.enableSpeakingHighlight),
				speakingOutlineColor: settings.speakingOutlineColor || 'rgba(255, 200, 0, 1)',
				enableLoadingHighlight: Boolean(settings.enableLoadingHighlight),
				loadingOutlineColor: settings.loadingOutlineColor || 'rgba(100, 180, 255, 1)',
				excludedChannels: settings.excludedChannels || [],
			};
			this.savedSettingsJson = JSON.stringify(this.settings);
		} catch(e) {
			this.error = e;
		} finally {
			this.loading--;
		}
	},
	computed: {
		hasUnsavedChanges() {
			if(this.savedSettingsJson === null) return false;
			return JSON.stringify(this.settings) !== this.savedSettingsJson;
		},
		previewSpeedLabel() {
			if(this.previewSpeed < 25) return "Slow";
			if(this.previewSpeed < 75) return "Normal";
			return "Fast";
		},
	},
	beforeDestroy() {
		window.removeEventListener('beforeunload', this._onBeforeUnload);
	},
	methods: {
		_onBeforeUnload(e) {
			if(this.hasUnsavedChanges) {
				e.preventDefault();
				e.returnValue = '';
			}
		},
		async save() {
			try {
				this.saving++;
				this.error = null;

				await this.$api.post('widgetSettings/chat', {
					...this.settings,
					ignoreUsers: (this.settings.ignoreUsers||'').split('\n').map(a => a.replace(/^\s*(.*?)\s*$/, '$1')).filter(a => !!a)
				});
				this.savedSettingsJson = JSON.stringify(this.settings);
			} catch(e) {
				console.error('Error saving widget:', e);
				this.saveError = e;
			} finally {
				this.saving--;
			}
		},
	}
};
</script>
